import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";

import vslThumb from "../assets/vsl-thumbnail.webp";

const VSL_THUMB = vslThumb;

export default function Hero() {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-12 md:pb-16 overflow-hidden bg-hero-glow"
    >
      <div aria-hidden className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none" />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 0%, transparent 60%, rgba(243,241,235,1) 100%)",
        }}
      />

      <div className="relative mx-auto max-w-[1100px] px-5 md:px-8 text-center">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 badge-pill max-w-[92vw] md:max-w-none"
          data-testid="hero-eyebrow"
        >
          <span className="eyebrow-dot shrink-0" />
          <span className="text-[#0a0a0a]/85 text-[10.5px] md:text-[12px] tracking-[0.16em] uppercase leading-snug font-medium">
            For coaches, consultants and experts
          </span>
        </motion.div>

        {/* Headline */}
        {/* Masked line-by-line reveal headline */}
        <motion.h1
          data-testid="hero-headline"
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: { staggerChildren: 0.14, delayChildren: 0.15 },
            },
          }}
          className="mt-8 md:mt-10 font-semibold tracking-tight text-[25px] leading-[1.12] sm:text-[34px] md:text-[42px] lg:text-[50px] max-w-6xl mx-auto text-center"
          aria-label="You may have a PhD in your expertise, but what's the point if you still don't know how to turn it into massive paying clients?"
        >
          {[
            { text: "You may have a PhD in your expertise", dim: false },
            { text: "but what's the point if you still don't know", dim: true },
            { text: "how to turn it into massive paying clients?", dim: false },
          ].map((line, i) => (
            <span
              key={i}
              className="block overflow-hidden pb-[0.08em]"
              aria-hidden="true"
            >
              <motion.span
                variants={{
                  hidden: { y: "110%" },
                  show: {
                    y: "0%",
                    transition: {
                      duration: 1.1,
                      ease: [0.76, 0, 0.24, 1],
                    },
                  },
                }}
                className={`block will-change-transform ${
                  line.dim ? "text-gradient-dim" : "text-gradient"
                }`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        {/* Sub */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: [0.65, 0, 0.35, 1] }}
          className="mt-8 text-[15px] md:text-[18px] text-[#0a0a0a]/70 max-w-3xl mx-auto leading-relaxed space-y-4"
          data-testid="hero-subheadline"
        >
          <p>
            At ViralX Media, we build that entire growth system for you —
            turning your expertise into an influential personal brand that
            consistently attracts qualified leads using agentic automation
            systems.
          </p>
        </motion.div>
      </div>

      {/* VSL */}
      <div className="relative mx-auto max-w-[1100px] px-5 md:px-8 mt-10 md:mt-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="relative rounded-[22px] overflow-hidden border border-black/10 bg-[#0a0a0a]"
          data-testid="vsl-frame"
          style={{
            boxShadow:
              "0 60px 100px -40px rgba(10,10,10,0.35), 0 0 0 1px rgba(255,255,255,0.03) inset",
          }}
        >
          <div className="relative aspect-[16/9]">
            <img
              src={VSL_THUMB}
              alt="VIRALX MEDIA growth systems preview"
              className="w-full h-full object-cover"
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />

            {!playing && (
              <button
                data-testid="vsl-play"
                aria-label="Play VSL"
                onClick={() => setPlaying(true)}
                className="absolute inset-0 flex items-center justify-center group"
              >
                <span className="vsl-play-btn relative flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/10 border border-white/20 backdrop-blur-md transition-transform duration-300 group-hover:scale-105">
                  <Play className="w-7 h-7 md:w-8 md:h-8 text-white fill-white ml-1" strokeWidth={0} />
                  <span className="absolute inset-0 rounded-full border border-white/30 animate-ping opacity-40" />
                </span>
              </button>
            )}

            {playing && (
              <div
                data-testid="vsl-placeholder"
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/95 text-center px-6"
              >
                <span
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] text-white/80"
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.14)",
                  }}
                >
                  Placeholder
                </span>
                <p className="text-white text-xl md:text-2xl font-medium max-w-md">
                  Share your VSL link — we&apos;ll embed it here.
                </p>
                <button
                  onClick={() => setPlaying(false)}
                  data-testid="vsl-close"
                  className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] text-white border border-white/25 hover:border-white/50 hover:bg-white/10 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Golden premium CTA below VSL — primary conversion driver */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-10 md:mt-12 flex flex-col items-center gap-4"
        >
          <a
            href="https://forms.tgc.chat/book-now"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-golden-cta"
            className="group relative inline-flex items-center gap-4 w-full sm:w-auto justify-center px-8 py-5 md:px-12 md:py-6 rounded-full font-semibold text-[17px] md:text-[21px] tracking-tight transition-transform hover:scale-[1.02] active:scale-[0.99]"
            style={{
              background:
                "linear-gradient(180deg, #f8e29a 0%, #e2be5f 45%, #c69a3c 100%)",
              color: "#0a0a0a",
              boxShadow:
                "0 30px 70px -20px rgba(212,175,55,0.7), 0 0 0 1px rgba(212,175,55,0.4) inset, 0 2px 0 rgba(255,255,255,0.45) inset",
            }}
          >
            <span
              aria-hidden
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 80% 100% at 50% 0%, rgba(255,255,255,0.45) 0%, transparent 60%)",
              }}
            />
            <span className="relative">Build My Client-Getting Brand</span>
            <span
              className="relative inline-flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-full transition-transform duration-300 group-hover:translate-x-1"
              style={{ background: "#0a0a0a", color: "#f6dd8b" }}
            >
              →
            </span>
          </a>

          {/* Trust + scarcity row */}
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2.5">
              <div className="flex -space-x-2">
                {[
                  "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg",
                  "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg",
                  "https://images.pexels.com/photos/1516680/pexels-photo-1516680.jpeg",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    className="w-6 h-6 rounded-full object-cover border-2 border-[#f3f1eb]"
                  />
                ))}
              </div>
              <span className="text-[#0a0a0a]/70 text-[13px] font-medium">
                Join 110+ experts scaling with us
              </span>
            </div>
            <p className="text-[#0a0a0a]/45 text-[12px] tracking-wide flex items-center gap-2">
              <span className="relative flex w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full bg-[#b98d34] animate-ping opacity-70" />
                <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-[#b98d34]" />
              </span>
              Apply to discover whether ViralX Media is the right growth partner for you.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
